import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api';
import { toast } from 'sonner';

import DiaryWizard from '@/components/site-diary/DiaryWizard';
import DiaryListView from '@/components/site-diary/DiaryListView';
import NotificationSettingsModal from '@/components/site-diary/NotificationSettingsModal';
import SectionAssignmentModal from '@/components/site-diary/SectionAssignmentModal';

export default function SiteDiaryPage() {
  const { user } = useAuth();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [diaries, setDiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [activeDiary, setActiveDiary] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [notifSettings, setNotifSettings] = useState({ enabled: false, recipient_user_ids: [], skip_weekends: false, skip_public_holidays: false });
  const [notifSaving, setNotifSaving] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [notifLog, setNotifLog] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [activeRoleFilter, setActiveRoleFilter] = useState(null);
  const [showSectionAssign, setShowSectionAssign] = useState(false);
  const [sectionAssignments, setSectionAssignments] = useState({});
  const [sectionAssignSaving, setSectionAssignSaving] = useState(false);

  // Wizard form state
  const [weather, setWeather] = useState({ rain: false, rain_mm: null, wind_strong: false, wind_kmh: null, storm: false, hours_lost: null, affected_work: false, temperature: null, humidity: null, wind_speed: null, description: '', affected_activities: '', affected_time_period: '', stoppage_type: '', evidence_urls: [], evidence_files: [] });
  const [labour, setLabour] = useState({ total_labour: 0, entries: [] });
  const [plantSummary, setPlantSummary] = useState({ plant_on_site: 0, selected_plant_ids: [], plant_entries: [], added: [], removed: [], breakdowns: [] });
  const [progress, setProgress] = useState([]);
  const [delays, setDelays] = useState([]);
  const [hse, setHse] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [generalNotes, setGeneralNotes] = useState('');
  const [subcontractors, setSubcontractors] = useState([]);
  const [plantRegister, setPlantRegister] = useState([]);
  const [diaryTasks, setDiaryTasks] = useState([]);
  const [uploading, setUploading] = useState(false);

  // ─── DATA LOADING ───
  useEffect(() => { loadProject(); }, [projectId]);
  useEffect(() => {
    if (projectId) { loadDiaries(); loadStats(); loadSubcontractors(); loadPlantRegister(); loadDiaryTasks(); loadSectionAssignments(); }
  }, [projectId]);

  const loadProject = async () => { try { const r = await api.get(`/projects/${projectId}`); setProject(r.data); } catch (e) { console.error(e); } };
  const loadSubcontractors = async () => { try { const r = await api.get(`/diary/subcontractors/${projectId}`); setSubcontractors(r.data.subcontractors || []); } catch (e) { console.error(e); } };
  const loadPlantRegister = async () => { try { const r = await api.get(`/diary/plant-register/${projectId}`); setPlantRegister(r.data.plant_items || []); } catch (e) { console.error(e); } };
  const loadDiaryTasks = async () => { try { const r = await api.get(`/diary/tasks/${projectId}`); setDiaryTasks(r.data.tasks || []); } catch (e) { console.error(e); } };
  const loadDiaries = async () => { setLoading(true); try { const r = await api.get(`/diary/projects/${projectId}?limit=50`); setDiaries(r.data.diaries || []); } catch (e) { console.error(e); } setLoading(false); };
  const loadStats = async () => { try { const r = await api.get(`/diary/stats/${projectId}`); setStats(r.data); } catch (e) { console.error(e); } };
  const loadNotifSettings = async () => { try { const r = await api.get(`/diary-notifications/settings/${projectId}`); setNotifSettings(r.data); } catch (e) { console.error(e); } };
  const loadTeamMembers = async () => { try { const r = await api.get('/users'); setTeamMembers(r.data.users || r.data || []); } catch (e) { console.error(e); } };
  const loadNotifLog = async () => { try { const r = await api.get(`/diary-notifications/log/${projectId}?limit=10`); setNotifLog(r.data.logs || []); } catch (e) { console.error(e); } };
  const loadSectionAssignments = async () => { try { const r = await api.get(`/diary/section-assignments/${projectId}`); setSectionAssignments(r.data.assignments || {}); } catch (e) { console.error(e); } };

  // ─── ACTIONS ───
  const uploadDiaryFile = async (file, category = 'diary') => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('category', category);
      const r = await api.post('/upload/multiple', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const uploaded = r.data.files || r.data.uploaded_files || [];
      setUploading(false);
      return uploaded.map(f => ({ name: f.filename || f.name || file.name, url: f.url || f.file_url || '', size: f.size || file.size }));
    } catch (e) { toast.error('Upload failed'); setUploading(false); return []; }
  };

  const createTaskFromDelay = async (diaryId, delayIndex) => {
    try { await api.post('/diary/delay-to-task', { diary_id: diaryId, delay_index: delayIndex }); toast.success('Task created from delay — check your notifications'); loadDiaryTasks(); } catch (e) { toast.error('Failed to create task'); }
  };

  const convertTaskToDelayEvent = async (taskId) => {
    try { await api.post(`/diary/tasks/${taskId}/convert-to-delay-event`); toast.success('Delay event created successfully'); loadDiaryTasks(); } catch (e) { toast.error('Failed to convert to delay event'); }
  };

  const saveSectionAssignments = async () => {
    setSectionAssignSaving(true);
    try {
      const assignments = Object.entries(sectionAssignments).map(([section, data]) => ({ section, user_id: data?.user_id || null, user_name: data?.user_name || null }));
      await api.put(`/diary/section-assignments/${projectId}`, { assignments });
      toast.success('Section assignments saved');
    } catch (e) { toast.error('Failed to save assignments'); }
    setSectionAssignSaving(false);
  };

  const assignSection = (sectionId, userId) => {
    const member = teamMembers.find(m => m.id === userId);
    setSectionAssignments(prev => ({ ...prev, [sectionId]: userId ? { user_id: userId, user_name: member?.name || member?.email || '' } : null }));
  };

  const assignAllSections = (userId) => {
    const ASSIGNABLE_STEPS = ['overview', 'weather', 'labour', 'plant', 'progress', 'delays', 'hse', 'materials', 'visitors', 'attachments'];
    const member = teamMembers.find(m => m.id === userId);
    const newAssignments = {};
    ASSIGNABLE_STEPS.forEach(s => { newAssignments[s] = userId ? { user_id: userId, user_name: member?.name || member?.email || '' } : null; });
    setSectionAssignments(newAssignments);
  };

  const markSectionComplete = async (sectionId) => {
    if (!activeDiary) return;
    try { const r = await api.post(`/diary/${activeDiary.id}/sections/complete`, { section: sectionId }); setActiveDiary(r.data); toast.success(`${sectionId} section marked complete`); } catch (e) { toast.error('Failed to mark complete'); }
  };

  const unmarkSectionComplete = async (sectionId) => {
    if (!activeDiary) return;
    try { const r = await api.post(`/diary/${activeDiary.id}/sections/uncomplete`, { section: sectionId }); setActiveDiary(r.data); toast.success(`${sectionId} section reopened`); } catch (e) { toast.error('Failed to reopen section'); }
  };

  const openNotifSettings = () => { loadNotifSettings(); loadTeamMembers(); loadNotifLog(); setShowNotifSettings(true); };
  const saveNotifSettings = async () => { setNotifSaving(true); try { await api.put(`/diary-notifications/settings/${projectId}`, notifSettings); toast.success('Notification settings saved'); } catch (e) { toast.error('Failed to save settings'); } setNotifSaving(false); };

  const openSectionAssign = () => { loadSectionAssignments(); loadTeamMembers(); setShowSectionAssign(true); };

  const createDiary = async () => {
    try { const r = await api.post('/diary', { project_id: projectId, date: selectedDate }); toast.success('Diary created'); openWizard(r.data); } catch (e) { toast.error('Failed to create diary'); }
  };

  const openWizard = (diary) => {
    setActiveDiary(diary);
    setWeather(diary.weather || { rain: false, wind_strong: false, storm: false, affected_work: false, temperature: null, humidity: null, wind_speed: null, description: '', affected_activities: '', affected_time_period: '', stoppage_type: '', evidence_urls: [] });
    setLabour(diary.labour || { total_labour: 0, entries: [] });
    setPlantSummary(diary.plant_summary || { plant_on_site: 0, added: [], removed: [], breakdowns: [] });
    setProgress(diary.progress || []);
    setDelays(diary.delays || []);
    setHse(diary.hse || []);
    setMaterials(diary.materials || []);
    setVisitors(diary.visitors || []);
    setGeneralNotes(diary.general_notes || '');
    setCurrentStep(0);
    setView('wizard');
  };

  const saveDraft = async () => {
    if (!activeDiary) return;
    setSaving(true);
    try {
      const payload = { status: 'draft', weather, labour, plant_summary: plantSummary, progress, delays, hse, materials, visitors, general_notes: generalNotes };
      const r = await api.put(`/diary/${activeDiary.id}`, payload);
      setActiveDiary(r.data);
      toast.success('Draft saved');
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  const fetchWeather = async () => {
    try {
      const lat = -26.2041; const lon = 28.0473;
      const r = await api.get(`/diary/weather/fetch?lat=${lat}&lon=${lon}`);
      const w = r.data;
      setWeather(prev => ({ ...prev, temperature: w.temperature, humidity: w.humidity, wind_speed: w.wind_speed, description: w.description, rain: w.rain, wind_strong: w.wind_strong, storm: w.storm }));
      toast.success('Weather data fetched');
    } catch (e) { toast.error('Weather fetch failed'); }
  };

  const generateAISummary = async () => {
    if (!activeDiary) return;
    setAiLoading(true);
    try { await saveDraft(); const r = await api.post(`/diary/${activeDiary.id}/ai-summary`); setActiveDiary(prev => ({ ...prev, ai_summary: r.data.summary, ai_risk_flags: r.data.risk_flags, ai_actions: r.data.actions })); toast.success('AI summary generated'); } catch (e) { toast.error('AI generation failed'); }
    setAiLoading(false);
  };

  const submitDiary = async () => {
    if (!activeDiary) return;
    try { await saveDraft(); await api.post(`/diary/${activeDiary.id}/submit`); toast.success('Diary submitted'); setView('list'); loadDiaries(); loadStats(); } catch (e) { toast.error('Submit failed'); }
  };

  const lockDiary = async (id) => { try { await api.post(`/diary/${id}/lock`); toast.success('Diary locked'); loadDiaries(); } catch (e) { toast.error('Lock failed'); } };

  // ─── ENTRY MANAGEMENT ───
  const addLabourEntry = () => setLabour(l => ({ ...l, entries: [...l.entries, { subcontractor: '', subcontractor_id: '', trade: '', count: 0, absent: 0, idle: 0, overtime_hours: 0, notes: '' }] }));
  const removeLabourEntry = (i) => setLabour(l => ({ ...l, entries: l.entries.filter((_, idx) => idx !== i) }));
  const updateLabourEntry = (i, field, val) => setLabour(l => {
    const entries = [...l.entries]; entries[i] = { ...entries[i], [field]: val };
    if (field === 'subcontractor_id' && val) { const sub = subcontractors.find(s => s.id === val); if (sub) { entries[i].subcontractor = sub.company_name || sub.name || ''; entries[i].trade = sub.discipline || ''; } }
    const total = entries.reduce((s, e) => s + (parseInt(e.count) || 0), 0);
    return { ...l, entries, total_labour: total };
  });

  const togglePlantItem = (plantId) => {
    setPlantSummary(p => {
      const ids = p.selected_plant_ids || []; const entries = p.plant_entries || [];
      if (ids.includes(plantId)) return { ...p, selected_plant_ids: ids.filter(id => id !== plantId), plant_entries: entries.filter(e => e.plant_id !== plantId), plant_on_site: Math.max(0, (p.plant_on_site || 0) - 1) };
      const item = plantRegister.find(pl => pl.id === plantId);
      const newEntry = { plant_id: plantId, plant_name: item?.name || '', type: item?.type || '', on_site: true, hours_worked: 0, hours_idle: 0, breakdown: false, breakdown_description: '', breakdown_duration: 0, work_stopped: false, notes: '' };
      return { ...p, selected_plant_ids: [...ids, plantId], plant_entries: [...entries, newEntry], plant_on_site: (p.plant_on_site || 0) + 1 };
    });
  };
  const updatePlantEntry = (plantId, field, val) => { setPlantSummary(p => ({ ...p, plant_entries: (p.plant_entries || []).map(e => e.plant_id === plantId ? { ...e, [field]: val } : e) })); };

  const addProgressEntry = () => setProgress(p => [...p, { activity: '', planned: '', actual: '', status: 'on_track', area: '', notes: '' }]);
  const removeProgressEntry = (i) => setProgress(p => p.filter((_, idx) => idx !== i));
  const updateProgressEntry = (i, field, val) => setProgress(p => { const n = [...p]; n[i] = { ...n[i], [field]: val }; return n; });

  const addDelayEntry = () => setDelays(d => [...d, { category: '', description: '', time_impact: '', hours_lost: 0, area: '', trade: '', activity: '', mitigation: '', task_id: '', converted_to_delay_event: false, delay_event_id: '' }]);
  const removeDelayEntry = (i) => setDelays(d => d.filter((_, idx) => idx !== i));
  const updateDelayEntry = (i, field, val) => setDelays(d => { const n = [...d]; n[i] = { ...n[i], [field]: val }; return n; });

  const addHseEntry = () => setHse(h => [...h, { entry_type: 'observation', description: '', severity: 'low', area: '', exact_location: '', date_time: '', incident_classification: '', injury_type: '', body_part_affected: '', treatment_type: '', days_lost: 0, persons_involved: '', persons_involved_details: [], witness_name: '', witness_contact: '', action_taken: '', corrective_actions: '', root_cause: '', investigation_required: false, investigation_details: '', reported_to_authority: false, authority_reference: '', evidence_files: [] }]);
  const removeHseEntry = (i) => setHse(h => h.filter((_, idx) => idx !== i));
  const updateHseEntry = (i, field, val) => setHse(h => { const n = [...h]; n[i] = { ...n[i], [field]: val }; return n; });

  const addMaterialEntry = () => setMaterials(m => [...m, { material: '', delivery_status: 'delivered', delivery_note_number: '', ordered_quantity: '', delivered_quantity: '', quantity: '', supplier: '', received_by: '', condition: 'good', issues: '', evidence_files: [], notes: '' }]);
  const removeMaterialEntry = (i) => setMaterials(m => m.filter((_, idx) => idx !== i));
  const updateMaterialEntry = (i, field, val) => setMaterials(m => { const n = [...m]; n[i] = { ...n[i], [field]: val }; return n; });

  const addVisitorEntry = () => setVisitors(v => [...v, { name: '', company: '', purpose: '', id_number: '', time_in: '', time_out: '', safety_induction_completed: false, induction_date: '', instructions_given: '' }]);
  const removeVisitorEntry = (i) => setVisitors(v => v.filter((_, idx) => idx !== i));
  const updateVisitorEntry = (i, field, val) => setVisitors(v => { const n = [...v]; n[i] = { ...n[i], [field]: val }; return n; });

  // ─── VIEW ROUTING ───
  if (view === 'wizard' && activeDiary) {
    return (
      <DiaryWizard
        activeDiary={activeDiary} setActiveDiary={setActiveDiary}
        currentStep={currentStep} setCurrentStep={setCurrentStep}
        project={project} user={user} projectId={projectId}
        weather={weather} setWeather={setWeather} fetchWeather={fetchWeather}
        labour={labour} addLabourEntry={addLabourEntry} removeLabourEntry={removeLabourEntry} updateLabourEntry={updateLabourEntry} subcontractors={subcontractors}
        plantSummary={plantSummary} plantRegister={plantRegister} togglePlantItem={togglePlantItem} updatePlantEntry={updatePlantEntry}
        progress={progress} addProgressEntry={addProgressEntry} removeProgressEntry={removeProgressEntry} updateProgressEntry={updateProgressEntry}
        delays={delays} addDelayEntry={addDelayEntry} removeDelayEntry={removeDelayEntry} updateDelayEntry={updateDelayEntry}
        hse={hse} addHseEntry={addHseEntry} removeHseEntry={removeHseEntry} updateHseEntry={updateHseEntry}
        materials={materials} addMaterialEntry={addMaterialEntry} removeMaterialEntry={removeMaterialEntry} updateMaterialEntry={updateMaterialEntry}
        visitors={visitors} addVisitorEntry={addVisitorEntry} removeVisitorEntry={removeVisitorEntry} updateVisitorEntry={updateVisitorEntry}
        generalNotes={generalNotes} setGeneralNotes={setGeneralNotes}
        saveDraft={saveDraft} submitDiary={submitDiary} generateAISummary={generateAISummary}
        createTaskFromDelay={createTaskFromDelay} convertTaskToDelayEvent={convertTaskToDelayEvent} uploadDiaryFile={uploadDiaryFile}
        saving={saving} aiLoading={aiLoading} uploading={uploading}
        sectionAssignments={sectionAssignments} markSectionComplete={markSectionComplete} unmarkSectionComplete={unmarkSectionComplete}
        onBack={() => setView('list')}
      />
    );
  }

  // ─── LIST VIEW ───
  return (
    <DashboardLayout>
      <DiaryListView
        project={project} projectId={projectId}
        diaries={diaries} loading={loading} stats={stats}
        selectedDate={selectedDate} setSelectedDate={setSelectedDate}
        sectionAssignments={sectionAssignments}
        openWizard={openWizard} setCurrentStep={setCurrentStep}
        createDiary={createDiary} lockDiary={lockDiary}
        openNotifSettings={openNotifSettings} openSectionAssign={openSectionAssign}
      />

      {showSectionAssign && (
        <SectionAssignmentModal
          sectionAssignments={sectionAssignments} teamMembers={teamMembers}
          assignSection={assignSection} assignAllSections={assignAllSections}
          saveSectionAssignments={saveSectionAssignments} sectionAssignSaving={sectionAssignSaving}
          onClose={() => setShowSectionAssign(false)}
        />
      )}

      {showNotifSettings && (
        <NotificationSettingsModal
          notifSettings={notifSettings} setNotifSettings={setNotifSettings}
          notifSaving={notifSaving} saveNotifSettings={saveNotifSettings}
          teamMembers={teamMembers} notifLog={notifLog}
          recipientSearch={recipientSearch} setRecipientSearch={setRecipientSearch}
          activeRoleFilter={activeRoleFilter} setActiveRoleFilter={setActiveRoleFilter}
          onClose={() => setShowNotifSettings(false)}
        />
      )}
    </DashboardLayout>
  );
}
